/* eslint-disable import/prefer-default-export */
import { defineStore } from "pinia";
import axios from "axios";

// Tracks the in-flight list request so a superseded one can be aborted. Kept
// outside state deliberately: it must not be reactive, and the store is a
// singleton so a module-level handle is scoped correctly.
let listRequestController = null;

/**
 * Builds the /emailLogs query string. Shared by the paged list and the export,
 * so the two can never drift into exporting a different set than the one on
 * screen - which is the usual way "export doesn't match the table" bugs start.
 */
function buildEmailLogsUrl(offset, limit, query, clientId, matterId, paymentStatus, messageType, range, scopeAccount, scope, orderby, sortAscending, search) {
  // URLSearchParams rather than string interpolation: `search` and `query` are
  // free text straight from the user, so a client called "Smith & Jones" or a
  // search for "invoice #12" used to terminate or corrupt the query string.
  // Encoding every value also means no future parameter can reintroduce that.
  const params = new URLSearchParams();
  params.set('offset', offset);
  params.set('limit', limit);
  params.set('query', query ?? '');
  if (clientId !== undefined) {
    params.set('clientId', clientId);
  }
  if (matterId !== undefined) {
    params.set('matterId', matterId);
  }
  if (search !== undefined) {
    params.set('search', search);
  }
  if (range !== undefined && range.startDate !== null) {
    params.set('startDate', new Date(range.startDate).toNetworkDate());
  }
  if (range !== undefined && range.endDate !== null) {
    const endDate = new Date(range.endDate);
    endDate.setDate(endDate.getDate() + 1);
    params.set('endDate', endDate.toNetworkDate());
  }
  if (orderby !== undefined) {
    params.set('orderBy', orderby);
  }
  if (sortAscending !== undefined) {
    params.set('sortAscending', sortAscending);
  }
  if (paymentStatus?.id) {
    params.set('paymentStatus', paymentStatus.id);
  }
  if (messageType?.id) {
    params.set('messageType', messageType.id);
  }
  if (scopeAccount?.id) {
    params.set('scopeAccount', scopeAccount.id);
    params.set('scope', scope);
  } else {
    params.set('scope', 'all');
  }
  return `/emailLogs?${params.toString()}`;
}
export const useInvoiceEmailActivityStore = defineStore("invoiceEmailActivityStore", {
  state: () => ({
    invoiceEmailsActivityList: [],
    count: 0,
    alerts: {},
    loading: false
  }),
  actions: {
    async getInvoiceEmailsActivity(offset, limit, query, clientId, matterId, paymentStatus, messageType, range, scopeAccount, scope, orderby, sortAscending, search) {
      // Abort whatever is still running. Without this, applying filter A and
      // then filter B can render A's results if A resolves last - the user
      // sees rows that don't match the filters on screen.
      listRequestController?.abort();
      const controller = new AbortController();
      listRequestController = controller;
      this.$patch({
        loading: true
      });
      try {
        const url = buildEmailLogsUrl(offset, limit, query, clientId, matterId, paymentStatus, messageType, range, scopeAccount, scope, orderby, sortAscending, search);
        const response = await axios({
          url,
          method: "GET",
          signal: controller.signal
        });

        // Aborting narrows the race but does not close it: abort() cannot
        // un-receive a response that already arrived, so a superseded request
        // can still resolve here and overwrite newer results. Only the request
        // that currently owns the handle is allowed to write to the store.
        if (listRequestController !== controller) return;
        response.data.items.forEach(x => {
          x.expanded = false;
          x.reminders.forEach(r => {
            r.expanded = false;
          });
        });
        this.$patch({
          invoiceEmailsActivityList: response.data.items,
          count: response.data.count
        });
        this.$patch({
          loading: false
        });
      } catch (error) {
        // A superseded request isn't a failure: a newer one is already in
        // flight and owns both the results and the loading state, so bail
        // without touching either.
        if (axios.isCancel(error)) return;
        this.alerts.error(error?.response?.data?.message);
        this.$patch({
          loading: false
        });
      } finally {
        // Only release the handle if it's still ours - a newer request may
        // have replaced it while this one was unwinding.
        if (listRequestController === controller) {
          listRequestController = null;
        }
      }
    },
    /**
     * Fetches every row matching the current filters, for CSV export.
     *
     * Deliberately returns the rows instead of patching them into state. The
     * page's memory problems come from Vue proxying and rendering thousands
     * of nested recipient/reminder arrays - a plain array that's mapped to
     * strings and then dropped costs almost nothing by comparison. Keep it
     * that way: do not route this through invoiceEmailsActivityList.
     */
    async fetchInvoiceEmailsForExport(totalCount, query, clientId, matterId, paymentStatus, messageType, range, scopeAccount, scope, orderby, sortAscending, search) {
      const url = buildEmailLogsUrl(0, totalCount, query, clientId, matterId, paymentStatus, messageType, range, scopeAccount, scope, orderby, sortAscending, search);
      const response = await axios({
        url,
        method: "GET"
      });
      return response.data.items || [];
    },
    /**
     * Drops the loaded page and aborts anything in flight. Call from the
     * page's onUnmounted: this store is a singleton, so without it the last
     * result set (and every nested recipient/reminder Pinia has wrapped in a
     * reactive proxy) stays resident for the rest of the session.
     */
    releaseInvoiceEmailsActivity() {
      listRequestController?.abort();
      listRequestController = null;
      this.$patch({
        invoiceEmailsActivityList: [],
        count: 0,
        loading: false
      });
    },
    async sendReminderEmail({
      timeSheetId,
      trustRequestId,
      customEmailId
    }) {
      let url = `/invoices/email?`;
      if (timeSheetId !== undefined) {
        url += `timeSheetId=${timeSheetId}`;
        url += `&messageType=2`;
      }
      if (customEmailId !== undefined) {
        url += `customEmailId=${customEmailId}`;
        url += `&messageType=2`;
      }
      if (trustRequestId !== undefined) {
        url = `/trustrequest/resend?trustRequestId=${trustRequestId}`;
      }
      await axios({
        url,
        method: "POST"
      });
    },
    async getEmailLogsByID(id) {
      try {
        const response = await axios({
          url: `/emailLogs/${id}`,
          method: "GET"
        });
        return response.data;
      } catch (error) {
        this.alerts.error(error.response.data.message);
      }
    }
  }
});